/**
 * Phase 5 — elkheir_v1 Backup/Export (Renderer sammelt, Electron Main schreibt Datei).
 */

import { getElkheirDb, DB_NAME } from './elkheirDb';
import { ENTITY_STORE_TABLE_NAMES } from './entityAggregateStore';
import { flushPendingStorageWrites } from './businessPayloadWrite';
import { normalizeBusinessStorageId, getCurrentBusinessId } from '../../utils/sharedCore';

const BACKUP_FORMAT = 'elkheir_storage_backup';
const BACKUP_VERSION = 1;

const CORE_TABLES = ['kv_items', 'meta', 'outbox', 'domain_aggregate'];

function isKvRowForBusiness(key, businessId) {
  if (!key || !businessId) return true;
  const id = normalizeBusinessStorageId(businessId);
  if (!id) return false;
  const patterns = [
    `business_${id}`,
    `biz:${id}`,
    `Elkheir:v1:meta:business_payload_v1:${id}`,
    id
  ];
  return patterns.some((p) => key.includes(p));
}

function isMetaRowForBusiness(key, businessId) {
  if (!businessId) return true;
  const id = normalizeBusinessStorageId(businessId);
  return !id || (key && key.includes(id));
}

/**
 * @param {{ businessId?: string, scope?: 'full'|'business' }} [options]
 */
export async function buildStorageBackupSnapshot(options = {}) {
  const businessId = options.businessId || getCurrentBusinessId({ allowFallback: true }) || null;
  const scope = options.scope || (businessId ? 'business' : 'full');

  await flushPendingStorageWrites();

  const db = getElkheirDb();
  await db.open();

  const tables = {};
  const tableNames = [...CORE_TABLES, ...ENTITY_STORE_TABLE_NAMES];

  for (const name of tableNames) {
    try {
      const table = db.table(name);
      if (!table) {
        tables[name] = [];
        continue;
      }

      if (scope === 'business' && businessId) {
        if (name === 'kv_items') {
          const all = await table.toArray();
          tables[name] = all.filter((row) => isKvRowForBusiness(row.key, businessId));
        } else if (name === 'meta') {
          const all = await table.toArray();
          tables[name] = all.filter((row) => isMetaRowForBusiness(row.key, businessId));
        } else if (name.startsWith('entity_') || name === 'domain_aggregate') {
          tables[name] = await table.where('businessId').equals(businessId).toArray();
        } else if (name === 'outbox') {
          tables[name] = await table.toArray();
        } else {
          tables[name] = await table.toArray();
        }
      } else {
        tables[name] = await table.toArray();
      }
    } catch {
      tables[name] = [];
    }

    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  let recordCount = 0;
  for (const rows of Object.values(tables)) {
    recordCount += Array.isArray(rows) ? rows.length : 0;
  }

  return {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    dbName: DB_NAME,
    exportedAt: new Date().toISOString(),
    scope,
    businessId: businessId || null,
    recordCount,
    tables
  };
}

function downloadJsonFallback(filename, json) {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * @param {{ businessId?: string, scope?: 'full'|'business' }} [options]
 */
export async function exportStorageBackupToFile(options = {}) {
  const snapshot = await buildStorageBackupSnapshot(options);
  const json = JSON.stringify(snapshot);
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const defaultName = `aman-backup-${snapshot.businessId || 'full'}-${stamp}.json`;

  if (typeof window !== 'undefined' && window.electronAPI?.saveFile && window.electronAPI?.writeTextFile) {
    const dialog = await window.electronAPI.saveFile({
      title: 'AMAN — Storage-Backup exportieren',
      defaultPath: defaultName,
      filters: [{ name: 'JSON Backup', extensions: ['json'] }]
    });
    if (dialog.canceled || !dialog.filePath) {
      return { ok: false, canceled: true };
    }
    const write = await window.electronAPI.writeTextFile(dialog.filePath, json);
    if (!write?.ok) {
      return { ok: false, error: write?.error || 'write_failed' };
    }
    return {
      ok: true,
      filePath: dialog.filePath,
      recordCount: snapshot.recordCount,
      businessId: snapshot.businessId,
      scope: snapshot.scope
    };
  }

  downloadJsonFallback(defaultName, json);
  return {
    ok: true,
    downloaded: true,
    recordCount: snapshot.recordCount,
    businessId: snapshot.businessId,
    scope: snapshot.scope
  };
}

function validateBackupSnapshot(snapshot) {
  if (!snapshot || snapshot.format !== BACKUP_FORMAT) {
    return { ok: false, error: 'invalid_format' };
  }
  if (!snapshot.tables || typeof snapshot.tables !== 'object') {
    return { ok: false, error: 'missing_tables' };
  }
  return { ok: true };
}

/**
 * @param {object} snapshot
 * @param {{ merge?: boolean }} [options]
 */
export async function restoreStorageBackupSnapshot(snapshot, options = {}) {
  const check = validateBackupSnapshot(snapshot);
  if (!check.ok) return check;

  await flushPendingStorageWrites();
  const db = getElkheirDb();
  await db.open();

  const tableNames = Object.keys(snapshot.tables);
  const existingTables = db.tables.map((t) => t.name).filter((n) => tableNames.includes(n));

  await db.transaction('rw', existingTables, async () => {
    for (const name of existingTables) {
      const rows = snapshot.tables[name];
      if (!Array.isArray(rows) || rows.length === 0) continue;
      const table = db.table(name);
      if (!options.merge) {
        const ids = rows.map((r) => r.id ?? r.key).filter(Boolean);
        if (name === 'kv_items' || name === 'meta') {
          for (const key of ids) {
            await table.delete(key);
          }
        } else if (ids.length > 0) {
          await table.bulkDelete(ids);
        }
      }
      await table.bulkPut(rows);
    }
  });

  return {
    ok: true,
    restoredTables: existingTables.length,
    recordCount: snapshot.recordCount || 0,
    businessId: snapshot.businessId || null
  };
}

/**
 * @param {{ merge?: boolean }} [options]
 */
export async function importStorageBackupFromFile(options = {}) {
  let json = null;
  let filePath = null;

  if (typeof window !== 'undefined' && window.electronAPI?.openFile && window.electronAPI?.readTextFile) {
    const dialog = await window.electronAPI.openFile({
      title: 'AMAN — Storage-Backup importieren',
      filters: [{ name: 'JSON Backup', extensions: ['json'] }]
    });
    if (dialog.canceled || !dialog.filePaths?.[0]) {
      return { ok: false, canceled: true };
    }
    filePath = dialog.filePaths[0];
    const read = await window.electronAPI.readTextFile(filePath);
    if (!read?.ok) {
      return { ok: false, error: read?.error || 'read_failed' };
    }
    json = read.content;
  } else {
    return { ok: false, error: 'electron_required', hint: 'Import nur in der Desktop-App' };
  }

  let snapshot;
  try {
    snapshot = JSON.parse(json);
  } catch {
    return { ok: false, error: 'invalid_json' };
  }

  const restored = await restoreStorageBackupSnapshot(snapshot, options);
  return { ...restored, filePath };
}
